import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { SessionUser, TeamCheck } from '@dpw/shared';
import { api } from '../api';
import { useChange, useCheck, useUsers } from '../lib/queries';
import { Avatar, ErrorNote, Loading } from '../ui/bits';
import { ago, agoStart } from '../lib/format';

const STATUS: Record<TeamCheck['status'], string> = { open: 'Waiting for answers', in_review: 'Waiting for your review', answered: 'Answered', conflict: 'Answers disagree', confirmed: 'Confirmed', expired: 'Nobody answered' };

export function CheckPage({ me }: { me: SessionUser }) {
  const { id = '' } = useParams();
  const q = useCheck(id);
  const users = useUsers();
  const [answer, setAnswer] = useState('');
  const [written, setWritten] = useState('');
  const [divertTo, setDivertTo] = useState('');
  const [back, setBack] = useState('');
  const send = useChange((t: string) => api.checks.answer(id, t));
  const confirm = useChange((b: { answerId?: string; text?: string }) => api.checks.confirm(id, b));
  const divert = useChange((u: string) => api.checks.divert(id, [u]));
  const sendBack = useChange((t: string) => api.checks.sendBack(id, t));
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const v = q.data;
  if (v.view === 'requester') {
    return (
      <>
        <div className="hdr"><div className="htl"><h1>Your question</h1></div></div>
        <div className="panel col gap12">
          <div className="sm"><b>{v.question}</b></div>
          {v.state === 'answered' ? <div className="aibox"><span className="k">Answer</span><div className="sm">{v.answer}</div></div> : <div className="sm mut">I don't have a confirmed answer yet, I'll get back to you.</div>}
          {v.conversationId && <Link className="link" to={`/assistant/${v.conversationId}`}>Open the conversation</Link>}
        </div>
      </>
    );
  }
  const c = v;
  const isRecipient = c.recipients.some(r => r.id === me.id);
  const isReviewer = c.reviewer?.id === me.id;
  const canDecide = c.requiresReview ? isReviewer : c.askedBy.id === me.id;
  const myFollowUp = c.recipients.find(r => r.id === me.id)?.followUp;
  const open = c.status === 'open';
  const err = send.error ?? confirm.error ?? divert.error ?? sendBack.error;
  const submit = (e: FormEvent) => { e.preventDefault(); if (answer.trim()) send.mutate(answer.trim(), { onSuccess: () => setAnswer('') }); };
  return (
    <>
      <div className="hdr"><div className="htl"><span className="xs mut">Team check /</span><h1 style={{ fontSize: 20 }}>{c.question}</h1><span className="pl">{STATUS[c.status]}</span>{c.hasConflict && c.status !== 'confirmed' && <span className="lbl risk">Answers disagree</span>}</div></div>
      <div className="metas">
        <div><b>Asked by</b><span>{c.askedBy.name}</span></div>
        {c.about && <div><b>About</b><span>{c.about.kind === 'activity' ? <Link to={`/activities/${c.about.id}`}>{c.about.title}</Link> : c.about.kind === 'track' ? <Link to={`/tracks/${c.about.id}`}>{c.about.title}</Link> : c.about.title}</span></div>}
        <div><b>Asked</b><span>{agoStart(c.createdAt)}</span></div>
        {c.requiresReview && <div><b>Review</b><span>{c.reviewer?.name ?? 'Director'} approves before the answer goes back</span></div>}
      </div>
      {c.suggestionNote && isReviewer && <div className="aibox"><span className="k">The assistant suggests</span><div className="sm">{c.suggestionNote}</div></div>}
      <div className="cols3">
        <div className="l">
          <div className="panel">
            <div className="sh" style={{ marginBottom: 6 }}><b>Answers</b><span className="mono">{c.answers.length}</span></div>
            {!c.answers.length && <div className="xs mut">No answers yet.</div>}
            {c.answers.map(a => (
              <div className="ans" key={a.id}>
                <Avatar p={a.user} />
                <div className="b"><div>{a.answer}</div><div className="xs mut">{a.user.name} · {ago(a.updatedAt)}</div></div>
                {canDecide && c.status !== 'confirmed' && <button className="bt sm" onClick={() => confirm.mutate({ answerId: a.id })} disabled={confirm.isPending}>{c.requiresReview ? 'Approve this' : 'This one is right'}</button>}
              </div>
            ))}
            {c.confirmedAnswer && <div className="okmsg" style={{ marginTop: 8 }}>Confirmed by {c.confirmedBy?.name}: {c.confirmedAnswer}</div>}
          </div>
          {isRecipient && open && (
            <form className="panel col gap12" onSubmit={submit}>
              {myFollowUp && <div className="aibox"><span className="k">Follow-up</span><div className="sm">{myFollowUp}</div></div>}
              <label className="fld">Your answer<textarea className="inp" rows={3} value={answer} onChange={e => setAnswer(e.target.value)} /></label>
              <div><button className="bt pri" disabled={send.isPending || !answer.trim()}>Send answer</button></div>
            </form>
          )}
          {canDecide && c.status !== 'confirmed' && (
            <form className="panel col gap12" onSubmit={e => { e.preventDefault(); if (written.trim()) confirm.mutate({ text: written.trim() }); }}>
              <label className="fld">{c.requiresReview ? 'Or write the answer to send back' : 'Or write the right answer'}<input className="inp" value={written} onChange={e => setWritten(e.target.value)} /></label>
              <div><button className="bt" disabled={!written.trim()}>{c.requiresReview ? 'Approve and send' : 'Confirm'}</button></div>
            </form>
          )}
        </div>
        <div className="r">
          <div className="panel">
            <div className="sh" style={{ marginBottom: 6 }}><b>Asked</b></div>
            {c.recipients.map(r => <div className="lrow" key={r.id}><Avatar p={r} /><span className="sm" style={{ flex: 1 }}>{r.name}</span><span className={`pl ${r.answered ? 'grn' : ''}`}>{r.answered ? 'Answered' : 'Waiting'}</span></div>)}
          </div>
          {isReviewer && c.status !== 'confirmed' && (
            <div className="panel col gap12">
              <label className="fld">Divert to a person
                <select className="inp" value={divertTo} onChange={e => setDivertTo(e.target.value)}>
                  <option value="">Choose someone</option>
                  {(users.data ?? []).filter(u => u.isActive && u.id !== me.id && u.id !== c.askedBy.id).map(u => <option key={u.id} value={u.id}>{u.name}{u.id === c.suggestedOwner?.id ? ' (suggested)' : ''}</option>)}
                </select>
              </label>
              <div><button className="bt" disabled={!divertTo || divert.isPending} onClick={() => divert.mutate(divertTo, { onSuccess: () => setDivertTo('') })}>Ask them</button></div>
              {c.status === 'in_review' && (
                <form className="col gap6" onSubmit={e => { e.preventDefault(); if (back.trim()) sendBack.mutate(back.trim(), { onSuccess: () => setBack('') }); }}>
                  <label className="fld">Send back with a question<input className="inp" value={back} onChange={e => setBack(e.target.value)} /></label>
                  <div><button className="bt" disabled={!back.trim()}>Send back</button></div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
      {err ? <ErrorNote error={err} /> : null}
    </>
  );
}
